import React from 'react';
import {
  Button,
  Typography,
  Box,
  Divider,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import TextInput from 'components/atoms/textInput';
import PassInput from 'components/atoms/passInput';
import validate from './validate';

const LoginFormView = ({
  onSubmit,
  onGoogleLogin,
  defaultEmail,
  setShowLogin,
  isLoading,
  loginError
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validate),
    defaultValues: {
      email: defaultEmail || '',
      password: ''
    }
  });

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
      }}
    >
      {loginError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {loginError}
        </Alert>
      )}

      <TextInput
        label={t('login.emailLabel')}
        name='email'
        control={control}
        error={errors?.email || !!loginError}
        defaultValue={defaultEmail}
        helperText={errors?.email?.message || ''}
        inputProps={{
          'aria-invalid': !!loginError || !!errors?.email
        }}
      />

      <PassInput
        label={t('login.passwordLabel')}
        name='password'
        control={control}
        error={errors?.password || !!loginError}
        helperText={errors?.password?.message || ''}
        inputProps={{
          'aria-invalid': !!loginError || !!errors?.password
        }}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 0.5
        }}
      >
        <Button
          variant="text"
          color="primary"
          onClick={() => navigate('/reset')}
          sx={{ textTransform: 'none' }}
        >
          {t('login.forgot')}
        </Button>
      </Box>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={isLoading}
        sx={{ py: 1 }}
      >
        {isLoading ? t('common.loading') : t('login.logIn')}
      </Button>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          my: 0.5
        }}
      >
        <Divider sx={{ flexGrow: 1 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mx: 2 }}
        >
          {t('login.or')}
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          '& > div': {
            display: 'flex !important',
            justifyContent: 'center !important',
            width: '100% !important'
          },
          '& > div > div': {
            margin: '0 auto !important'
          }
        }}
      >
        <GoogleLogin
          onSuccess={onGoogleLogin}
          onError={() => {}}
          theme="filled_blue"
          text="signin_with"
          shape="rectangular"
          width="100%"
        />
      </Box>
      
      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('login.noAccount')}{' '}
          <Typography
            component="span"
            color="primary"
            sx={{
              cursor: 'pointer',
              fontWeight: 600
            }}
            onClick={() => setShowLogin(false)}
          >
            {t('login.signUp')}
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginFormView;